/**
 * Realtime connection recovery policy
 *
 * A browser throttles timers in a tab that has been hidden for a few minutes to roughly one tick a
 * minute. That makes the client believe the machine woke from sleep, re-validate its token and
 * restart a perfectly healthy realtime connection - once a minute, for as long as the tab stays in
 * the background. Every restart drops the events published while it is gone.
 *
 * Electron runs with backgroundThrottling disabled and is not affected, so it keeps the original
 * behaviour untouched.
 */

export interface RestartAfterLoginState {
	/** Running inside the Electron wrapper rather than a browser tab */
	embedded: boolean
	/** Realtime is currently connected */
	connected: boolean
	/** The token differs from the one the current connection authenticated with */
	tokenChanged: boolean
}

/**
 * Decide whether Auth:LoggedIn should restart the realtime connection
 * @param state Current connection state
 * @returns True if the connection should be restarted
 */
export function shouldRestartAfterLogin({ embedded, connected, tokenChanged }: RestartAfterLoginState) {
	if (embedded) { return true }
	return !connected || tokenChanged
}

export interface ReconnectOnVisibleState {
	/** Running inside the Electron wrapper rather than a browser tab */
	embedded: boolean
	/** Realtime is supposed to be running - false when logged out */
	running: boolean
	/** Realtime is currently connected */
	connected: boolean
}

/**
 * Decide whether a tab becoming visible again should reconnect immediately, rather than waiting for
 * the reconnect backoff - the user is looking at the dashboard now, so stale calls are visible.
 * @param state Current connection state
 * @returns True if the connection should be restarted
 */
export function shouldReconnectOnVisible({ embedded, running, connected }: ReconnectOnVisibleState) {
	if (embedded) { return false }
	return running && !connected
}
